import Queue from "queue";
import { parseLinkHeader } from "@web3-storage/parse-link-header";
import { withSignal } from "../utils";
import transformExportData from "./transformExportData";
import {
  API_RETRY_COUNT,
  QUEUE_STUCK_TIMEOUT,
  REJECT_REASON_ABORT,
  REJECT_REASON_API,
  REJECT_REASON_TIMEOUT,
} from "../constants";

function getQueryParams({ cursor, filterParams = [], itemsPerPage }) {
  return [
    { key: "page_size", value: itemsPerPage },
    { key: "pager", value: "cursor" },
    { key: "cursor", value: cursor },
    ...filterParams,
  ];
}

export default async function fetchDataForExport({
  abortController,
  exportStats = {},
  fetchFn,
  filterParams,
  onProgress,
  onTruncate,
  transforms,
}) {
  return new Promise(async (resolve, reject) => {
    exportStats.countOfAPIFailures = 0;
    let stopFetching = abortController?.signal?.aborted || false;
    const processQueue = new Queue({
      autostart: true,
      concurrency: 1,
      timeout: QUEUE_STUCK_TIMEOUT,
    });

    abortController?.signal?.addEventListener("abort", () => {
      stopFetching = true;
      processQueue.end();
      reject(abortController?.signal?.reason);
    });

    processQueue.on("error", (error) => {
      stopFetching = true;
      processQueue.end(error);
    });

    processQueue.on("timeout", () => {
      stopFetching = true;
      processQueue.end(REJECT_REASON_TIMEOUT);
    });

    processQueue.on("end", (error) => {
      if (error) {
        reject(error);
      }

      if (
        exportStats.countOfFilesDownloaded >= exportStats.totalFilesToDownload
      ) {
        resolve();
      }
    });

    while (!stopFetching) {
      const queryParams = getQueryParams({
        cursor: exportStats.cursor,
        filterParams,
        itemsPerPage: exportStats.itemsPerPage,
      });
      const response = await fetchFn(queryParams, abortController?.signal);
      const linkHeader = response?.headers?.get("link");

      if (linkHeader) {
        exportStats.countOfAPIFailures = 0;
      } else {
        exportStats.countOfAPIFailures++;
        if (exportStats.countOfAPIFailures > API_RETRY_COUNT) {
          stopFetching = true;
          exportStats.isPartialDownload = true;
        } else {
          continue;
        }
      }

      if (stopFetching) {
        const reason =
          exportStats.countOfAPIFailures > API_RETRY_COUNT
            ? REJECT_REASON_API
            : REJECT_REASON_ABORT;

        reject(reason);
        if (reason === REJECT_REASON_ABORT) {
          break;
        }
      }

      processQueue.push(() =>
        withSignal(
          transformExportData({
            abortController,
            chunk: response?.data,
            chunkNumber: exportStats.currentPageNumber,
            exportStats,
            onTruncate,
            transforms,
          }),
          abortController?.signal
        )
      );

      exportStats.currentPageNumber++;
      onProgress?.(
        (exportStats.currentPageNumber / exportStats.totalPagesToFetch) * 100
      );

      const parsed = parseLinkHeader(linkHeader);
      exportStats.cursor = parsed?.["'next'"]?.cursor;

      if (!exportStats.cursor) {
        break;
      }
    }
  });
}
